import { type Repub, REPUBS } from '~/vendor/cx-modules/constants';

import type { OddsFormatUppercase } from './site-config-api';

export const ODDS_FORMAT = {
  AMERICAN: 'american',
  DECIMAL: 'decimal',
  FRACTIONAL: 'fractional',
  TRADITIONAL: 'traditional',
} as const;

export type OddsFormat = (typeof ODDS_FORMAT)[keyof typeof ODDS_FORMAT];

const FORMAT_BY_UPPERCASE: Record<OddsFormatUppercase, OddsFormat> = {
  AMERICAN: ODDS_FORMAT.AMERICAN,
  DECIMAL: ODDS_FORMAT.DECIMAL,
  FRACTIONAL: ODDS_FORMAT.FRACTIONAL,
};

/**
 * The odds formats this repub offers, in the order the header shows them. Prefers the explicit
 * SiteConfig list; the repub fallback is only a safety net for repubs whose SiteConfig supplies none.
 *
 * Lives here rather than beside `getOddsFormatOptions` (packages/components) because server code has
 * to answer the same question without pulling in React: a cookie value naming a format this repub
 * does not offer must not be honoured, or the header shows a selection the dropdown cannot clear.
 * `getOddsFormatOptions` decorates this list with labels and test ids, so the two cannot disagree.
 */
export const getAvailableOddsFormats = (repub?: Repub, oddsFormatOptions?: OddsFormatUppercase[]): OddsFormat[] => {
  if (oddsFormatOptions?.length) {
    return oddsFormatOptions.map(format => FORMAT_BY_UPPERCASE[format]).filter(Boolean);
  }

  return repub === REPUBS.OC.name
    ? [ODDS_FORMAT.DECIMAL, ODDS_FORMAT.FRACTIONAL]
    : [ODDS_FORMAT.AMERICAN, ODDS_FORMAT.DECIMAL, ODDS_FORMAT.FRACTIONAL];
};

export const getOddsFormatIs = (userOddsFormat: OddsFormat) => ({
  American: userOddsFormat === ODDS_FORMAT.AMERICAN,
  Decimal: userOddsFormat === ODDS_FORMAT.DECIMAL,
  Fractional: userOddsFormat === ODDS_FORMAT.FRACTIONAL,
});

export type OddsFormatIs = ReturnType<typeof getOddsFormatIs>;
